import type { FilePublic } from '@repo/types/files';

import { FileType } from '@repo/types/enums/fileType';

export type MediaFileType = Exclude<FileType, FileType.FILE>;

export type GetServeFileTypeOptions = {
  omit?: MediaFileType[];
  pick?: MediaFileType[];
};

const imageMimeTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];

const videoMimeTypes: string[] = ['video/mp4', 'video/webm', 'video/3gp', 'video/ogg', 'video/avi', 'video/mpeg'];

const audioMimeTypes: string[] = ['audio/ogg', 'audio/3gp', 'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/webm', 'audio/flac', 'audio/object'];

const applicationMimeTypes: string[] = ['application/pdf'];

export class FileTypeUtils {
  private static mimeTypeGroups: Record<MediaFileType, string[]> = {
    [FileType.IMAGE]: imageMimeTypes,
    [FileType.VIDEO]: videoMimeTypes,
    [FileType.AUDIO]: audioMimeTypes,
    [FileType.APPLICATION]: applicationMimeTypes,
  };

  static getMimeTypeStructure(mimeType: string) {
    const [type, subtypeAndParams = ''] = mimeType.split('/');
    const [subtype, ...paramsArray] = subtypeAndParams.split(';');
    return { type, subtype, params: paramsArray };
  }

  static getServeFileType(mimeType: string, options?: GetServeFileTypeOptions): FileType {
    const { omit = [], pick = [] } = options || {};

    for (const [fileType, mimeTypes] of Object.entries(this.mimeTypeGroups)) {
      if (mimeTypes.includes(mimeType)) {
        if (pick.length > 0 && !pick.includes(fileType as MediaFileType)) {
          continue;
        }
        if (omit.length > 0 && omit.includes(fileType as MediaFileType)) {
          continue;
        }
        return fileType as FileType;
      }
    }

    return FileType.FILE;
  }

  static getFileType(file?: File | FilePublic, options?: GetServeFileTypeOptions) {
    if (!file) {
      return {
        mimeType: '',
        serveType: FileType.FILE,
        type: '',
        subtype: '',
        params: [],
      };
    }

    const isFileInstance = file instanceof File;
    const mimeType = isFileInstance ? file.type : file.fileType;

    const mimeTypeStruct = this.getMimeTypeStructure(mimeType);
    const serveType = this.getServeFileType(mimeType, options);

    return { mimeType, serveType, ...mimeTypeStruct };
  }
}
